import { Link } from "react-router-dom";
import { Phone, FileUp, CheckCircle2, Star, ShieldCheck, ArrowRight, CalendarCheck } from "lucide-react";
import TrustBadges from "@/components/TrustBadges";
import { BUSINESS, HOW_IT_WORKS, WHY_US, SERVICES, REVIEWS, FAQS } from "@/lib/constants";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-white overflow-hidden border-b border-slate-200">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-gold/15 border border-gold/50 text-black rounded-full px-4 py-2 text-sm sm:text-base font-semibold mb-6">
              <Star className="w-4 h-4 fill-gold text-gold" /> 4.9 Google Rating · 11 Years in Business
            </div>
            <h1 data-testid="hero-headline" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-black">
              Got a Traffic Ticket?<br />
              <span className="text-gold">We Can Help.</span>
            </h1>
            <p data-testid="hero-subheadline" className="mt-6 text-xl sm:text-2xl text-gray-800 leading-relaxed max-w-2xl">
              Protect your license, reduce your fines, and let our experienced team
              handle your traffic ticket.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4">
              <Link to="/consultation" data-testid="hero-consult-btn" className="btn-primary text-lg sm:text-xl">
                <CalendarCheck className="w-6 h-6" />
                Book a Free Consultation
              </Link>
              <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="hero-call-btn" className="btn-primary text-lg sm:text-xl">
                <Phone className="w-6 h-6" />
                Call {BUSINESS.phone}
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-800">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-gold" /> Most Tickets Reduced</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-gold" /> Open Daily 8 AM – 10 PM</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-gold" /> Fast Response</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 relative z-10">
        <TrustBadges />
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-black">How It Works</h2>
          <p className="mt-4 text-xl text-gray-700">Simple, fast, and stress-free.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.step} data-testid={`step-${s.step}`} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 rounded-full bg-gold text-black font-heading font-bold text-xl flex items-center justify-center mb-4">
                {s.step}
              </div>
              <h3 className="font-heading text-xl font-semibold text-navy">{s.title}</h3>
              <p className="mt-2 text-gray-700 text-base leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/consultation" data-testid="howitworks-consult-btn" className="btn-primary text-lg sm:text-xl">
            <CalendarCheck className="w-6 h-6" /> Book a Free Consultation
          </Link>
        </div>
      </section>

      {/* Services preview */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-black">Tickets We Handle</h2>
            <p className="mt-4 text-xl text-gray-700">We defend against all major Alberta traffic charges.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md hover:border-gold transition-all group"
              >
                <h3 className="font-heading text-xl font-semibold text-navy group-hover:text-gold transition-colors">{s.name}</h3>
                <p className="mt-2 text-gray-700 text-base">{s.description}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-navy font-semibold">
                  Learn more <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/services" data-testid="all-services-btn" className="btn-primary text-lg">
              See All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-black">Why Choose Us</h2>
            <p className="mt-4 text-xl text-gray-700">
              Over 11 years protecting Alberta drivers — thousands of clients helped,
              most tickets reduced.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {WHY_US.map((w) => (
                <li key={w} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-gold mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-900">{w}</span>
                </li>
              ))}
            </ul>
            <Link to="/consultation" data-testid="whyus-consult-btn" className="btn-primary mt-8">
              <CalendarCheck className="w-5 h-5" /> Book a Free Consultation
            </Link>
          </div>
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/8369211/pexels-photo-8369211.jpeg"
              alt="Professional defense team"
              className="rounded-lg shadow-lg w-full object-cover"
              style={{ maxHeight: 480 }}
            />
            <div className="absolute -bottom-6 -left-6 bg-gold text-black p-6 rounded-lg shadow-lg max-w-xs hidden sm:block">
              <div className="font-heading text-3xl font-bold">11 Years</div>
              <div className="font-semibold">Defending Alberta Drivers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews preview */}
      <section className="bg-white border-t border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-gold text-2xl">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-7 h-7 fill-gold text-gold" />)}
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 text-black">4.9 Google Rating</h2>
            <p className="mt-3 text-xl text-gray-700">What our clients say about us</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {REVIEWS.slice(0, 3).map((r, i) => (
              <div key={i} data-testid={`review-card-${i}`} className="bg-white border border-slate-200 text-black p-6 rounded-lg shadow-sm">
                <div className="flex gap-1 text-gold">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-5 h-5 fill-gold text-gold" />)}
                </div>
                <p className="mt-3 text-lg text-gray-800 leading-relaxed">"{r.text}"</p>
                <p className="mt-4 font-semibold text-black">— {r.name}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/reviews" data-testid="all-reviews-btn" className="btn-primary">Read More Reviews</Link>
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-black">Common Questions</h2>
          <p className="mt-4 text-xl text-gray-700">Straight answers, no legal jargon.</p>
        </div>
        <Accordion type="single" collapsible className="mt-10" data-testid="home-faq">
          {FAQS.slice(0, 5).map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-lg sm:text-xl font-semibold text-navy hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-lg text-gray-800 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-8 text-center">
          <Link to="/faq" className="btn-primary" data-testid="all-faq-btn">View All Questions</Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white border-t border-slate-200 py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-black">
            Ready to Fight Your Ticket?
          </h2>
          <p className="mt-4 text-xl text-gray-700">
            Submit your ticket now or call us. We're open daily 8 AM – 10 PM.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/consultation" data-testid="final-consult-btn" className="btn-primary text-lg sm:text-xl">
              <CalendarCheck className="w-6 h-6" /> Book a Free Consultation
            </Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="final-call-btn" className="btn-primary text-lg sm:text-xl">
              <Phone className="w-6 h-6" /> Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
